import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IconButton, InputAdornment, Stack } from '@mui/material'
import Iconify from '../../../components/Iconify'
import { LoadingButton } from '@mui/lab'
import * as yup from 'yup'
import i18n from '../../../i18n'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import api from '../../../services/api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { FormInputText } from 'components/FormInputText'

const signUpSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required(() => i18n.t<string>('general.validator.empty'))
    .min(3, () => i18n.t('general.validator.minCharacter', { number: 3 })),
  email: yup
    .string()
    .trim()
    .lowercase()
    .required(() => i18n.t<string>('general.validator.empty'))
    .email(() => i18n.t<string>('general.validator.invalidEmail')),
  password: yup
    .string()
    .trim()
    .required(() => i18n.t('general.validator.empty'))
    .min(6, () => i18n.t<string>('general.validator.minCharacter', { number: 6 }))
})

export interface SignUpFormProps {
  name: string
  email: string
  password: string
  createdAt: Date
  updatedAt?: string
  isVerified: boolean
  code?: string
  id?: number
}

export default function SignUpForm () {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { control, handleSubmit, formState: { errors } } = useForm<SignUpFormProps>({
    resolver: yupResolver(signUpSchema)
  })

  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleCreate = async (dataPost: SignUpFormProps) => {
    try {
      setIsLoading(true)
      const { data } = await api.post('/professionals', dataPost)
      if (data) {
        setIsLoading(false)
        navigate('/Verification', { state: { id: data.id, userId: data.userId, email: dataPost.email } })
        return
      }
    } catch (e: any) {
      setIsLoading(false)
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <>
      <Stack spacing={3}>
        <FormInputText
          name="name"
          required
          value={searchParams.get('nome') ?? ''}
          control={control}
          label={t<string>('signUp.field.name.label')}
          errorMessage={errors.name?.message}
        />
        <FormInputText
          name="email"
          required={true}
          value={searchParams.get('email') ?? ''}
          control={control}
          label={t<string>('signUp.field.email.label')}
          errorMessage={errors.email?.message}
        />
        <FormInputText
          name="password"
          required
          type={showPassword ? 'text' : 'password'}
          control={control}
          label={t<string>('signIn.field.password.label')}
          errorMessage={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>
      <Stack sx={{ mt: '20px' }}>
        <LoadingButton fullWidth size="large" type="submit" variant="contained" onClick={handleSubmit(handleCreate)}
          loading={isLoading}>
          {t('signUp.btn.create')}
        </LoadingButton>
      </Stack>
    </>
  )
}
